import type { INavListItem } from '@/api/types/public';
import ImageNavigationNav from './ImageNavigationNav.pc';
import LensProcessNavPc from './LensProcessNav.pc';
import ResourceSlotNav from './ResourceSlotNav.pc';
import styles from './index.module.scss';
import TextNav from './TextNav';

interface SideNavDropdownProps {
  navData: INavListItem[];
  isDarkMode: boolean;
  activeTabIndex: number;
  showDropdown: boolean;
  onDropdownMouseEnter: () => void;
  onDropdownMouseLeave: () => void;
}

const SideNavDropdown = ({
  navData,
  isDarkMode,
  activeTabIndex,
  showDropdown,
  onDropdownMouseEnter,
  onDropdownMouseLeave,
}: SideNavDropdownProps) => {
  return (
    <>
      {navData.map((navItem, index) => {
        if (!navItem.children || navItem.children.length === 0) return null;

        const isActive = showDropdown && activeTabIndex === index;

        const textNavItems = navItem.children.filter((item) => item.template_id === 2);
        const lensProcessItems = navItem.children.filter((item) => item.template_id === 4);
        const resourceSlotItems = navItem.children.filter(
          (item) => item.template_id === 5 && item.content?.resource_image,
        );
        const imageNavigationItems = navItem.children.filter(
          (item) =>
            item.template_id === 3 &&
            item.content?.list?.some((listItem) => Boolean(listItem.jump_image)),
        );
        const hasResourceAndImageNavigation =
          resourceSlotItems.length > 0 && imageNavigationItems.length > 0;
        const hasLensProcess = lensProcessItems.length > 0;

        return (
          <div
            key={`dropdown-${navItem.id}`}
            className={`absolute left-[0] top-[60px] z-[202] w-screen ${isDarkMode ? 'bg-[#131415]' : 'bg-white'} flex items-stretch justify-between box-border shadow-md ${hasResourceAndImageNavigation ? 'flex-wrap' : ''}`}
            style={{
              boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
              display: isActive ? 'flex' : 'none',
            }}
            onMouseEnter={onDropdownMouseEnter}
            onMouseLeave={onDropdownMouseLeave}
          >
            <div
              className={`flex min-w-0 flex-1 items-start gap-[clamp(40px,8.333vw,160px)] overflow-hidden px-[48px] pt-[20px] ${hasLensProcess || (imageNavigationItems.length > 0 && resourceSlotItems.length === 0) ? 'pb-[32px]' : 'pb-[24px]'}`}
            >
              {textNavItems.map((item, idx) => {
                return (
                  <div key={`${item.id}-${item.template_id}`} className="flex-shrink-0">
                    <TextNav
                      item={item}
                      firstLevelIndex={index}
                      firstLevelTitle={navItem.content?.nav_name || ''}
                      secondLevelIndex={idx}
                      isLast={idx === textNavItems.length - 1}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                );
              })}
            </div>

            {hasLensProcess ? (
              <div className={`ml-auto flex shrink-0 items-stretch ${styles.lensProcessContainer}`}>
                {lensProcessItems.map((item) => (
                  <LensProcessNavPc
                    key={`${item.id}-${item.template_id}`}
                    isDarkMode={isDarkMode}
                  />
                ))}
                {resourceSlotItems.map((item, resourceIndex) => (
                  <ResourceSlotNav
                    key={`${item.id}-${item.template_id}`}
                    item={item}
                    firstLevelIndex={index}
                    firstLevelTitle={navItem.content?.nav_name || ''}
                    resourceIndex={resourceIndex}
                  />
                ))}
              </div>
            ) : resourceSlotItems.length > 0 ? (
              <div className={`ml-auto flex shrink-0 ${styles.resourceSlotContainer}`}>
                {resourceSlotItems.map((item, resourceIndex) => (
                  <ResourceSlotNav
                    key={`${item.id}-${item.template_id}`}
                    item={item}
                    firstLevelIndex={index}
                    firstLevelTitle={navItem.content?.nav_name || ''}
                    resourceIndex={resourceIndex}
                  />
                ))}
              </div>
            ) : null}

            {imageNavigationItems.length > 0 ? (
              <div
                className={`flex shrink-0 items-start gap-[32px] pb-[32px] pt-[20px] ${
                  hasResourceAndImageNavigation
                    ? 'w-full justify-end px-[48px]'
                    : 'ml-auto pr-[48px]'
                }`}
              >
                {imageNavigationItems.map((item) => (
                  <ImageNavigationNav
                    key={`${item.id}-${item.template_id}`}
                    item={item}
                    firstLevelIndex={index}
                    firstLevelTitle={navItem.content?.nav_name || ''}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
};

export default SideNavDropdown;
